import React from 'react';
import { useEffect, useRef } from 'react';
import { Select, SelectProps, Group, Badge, Loader } from '@mantine/core';
import { GenevaApiItem, isActiveGenevaLookupItem, useGenevaLookup } from '../hooks/useGenevaLookup';

interface GenevaApiSelectProps extends Omit<SelectProps, 'data'> {
  linkTable: string;
  showApiLabel?: boolean;
  onLoadingChange?: (loading: boolean) => void;
  onSelectItem?: (item: GenevaApiItem | null) => void;
}

export const GenevaApiSelect: React.FC<GenevaApiSelectProps> = ({
  linkTable,
  showApiLabel = false,
  onLoadingChange,
  onSelectItem,
  label,
  ...selectProps
}) => {
  const { getOptions, isLoading, hasData, lookupData } = useGenevaLookup([linkTable]);

  const selectData = getOptions(linkTable);
  const loading = isLoading(linkTable);
  const lastReportedRef = useRef<string | null>(null);

  const { onChange, value, ...restSelectProps } = selectProps;

  const resolveItemForValue = (targetValue: string | null): GenevaApiItem | null => {
    if (!targetValue) return null;
    const items = (lookupData[linkTable] || []).filter(isActiveGenevaLookupItem);
    const normalized = targetValue.trim().toLowerCase();
    return (
      items.find(item => {
        const key = (item.CODE || item.NAMESORT || item.MAKENAME)?.trim().toLowerCase();
        return key === normalized;
      }) || null
    );
  };

  // Notify parent of loading state changes
  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  // Keep parent informed about the currently resolved lookup item
  useEffect(() => {
    if (!onSelectItem) return;
    const currentItem = resolveItemForValue(typeof value === 'string' ? value : value ?? null);
    const key = currentItem ? (currentItem.CODE || currentItem.NAMESORT || currentItem.MAKENAME || '') : '::null';
    if (lastReportedRef.current === key) {
      return;
    }
    lastReportedRef.current = key;
    onSelectItem(currentItem);
  }, [value, lookupData, linkTable, onSelectItem]);

  const enhancedLabel = showApiLabel && label ? (
    <Group gap="xs">
      {label}
      {/* <Badge size="xs" color="green" variant="outline">API Lookup</Badge> */}
      {loading && <Loader size="xs" />}
    </Group>
  ) : label;

  return (
    <Select
      {...restSelectProps}
      value={value}
      label={enhancedLabel}
      data={selectData}
      placeholder={loading ? "Loading..." : selectProps.placeholder}
      disabled={selectProps.disabled || loading || !hasData(linkTable)}
      searchable
      clearable
      rightSection={loading ? <Loader size="xs" /> : selectProps.rightSection}
      error={selectData.length === 0 && !loading && hasData(linkTable) ? 'No valid options available' : selectProps.error}
      comboboxProps={{
        width: 'auto',
        position: 'bottom-start',
        ...selectProps.comboboxProps
      }}
      onChange={(newValue, option) => {
        const item = resolveItemForValue(newValue);
        lastReportedRef.current = item ? (item.CODE || item.NAMESORT || item.MAKENAME || '') : '::null';
        onSelectItem?.(item);
        onChange?.(newValue, option);
      }}
    />
  );
};
